import React from 'react';
import LegalPageLayout, { Section, Paragraph, List } from './LegalPageLayout';

const AccessibilityStatement = () => {
  return (
    <LegalPageLayout title="Accessibility Statement" lastUpdated="November 11, 2025">
      {/* Commitment */}
      <Section title="Our Commitment to Accessibility">
        <Paragraph>
          DIZEE is committed to ensuring digital accessibility for people with disabilities. We are continually improving the user experience for everyone and applying the relevant accessibility standards to ensure we provide equal access to all of our users.
        </Paragraph>
        <Paragraph>
          We believe that everyone should be able to use our platform with ease, regardless of their abilities or the technology they use to access it.
        </Paragraph>
      </Section>

      {/* Conformance Status */}
      <Section title="Conformance Status">
        <div className="p-6 bg-[rgba(34,197,94,0.1)] border border-[rgba(34,197,94,0.3)] rounded">
          <div className="flex items-center gap-3 mb-4">
            <svg className="w-8 h-8 text-[#22c55e]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="text-[14px] font-medium text-white">WCAG 2.2 Level AA Conformance</h3>
          </div>
          <p className="text-[11px]">
            DIZEE conforms to the <strong className="text-white">Web Content Accessibility Guidelines (WCAG) 2.2</strong> at{' '}
            <strong className="text-white">Level AA</strong>. These guidelines explain how to make web content more accessible for people with disabilities and user-friendly for everyone.
          </p>
        </div>

        <div className="mt-6">
          <p className="text-[11px] font-medium text-white mb-2">What this means:</p>
          <List items={[
            'Level A: Basic web accessibility features (met)',
            'Level AA: Deals with the biggest and most common barriers for disabled users (met)',
            'Level AAA: Highest level of accessibility (partially met)'
          ]} />
        </div>
      </Section>

      {/* Accessibility Features */}
      <Section title="Accessibility Features">
        <Paragraph>We have implemented the following accessibility features:</Paragraph>
        
        <div className="mt-6 space-y-3">
          <div className="p-4 bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.1)] rounded">
            <h4 className="text-[12px] font-medium text-white mb-2">⌨️ Keyboard Navigation</h4>
            <p className="text-[10px]">All interactive elements can be accessed and operated using only a keyboard (Tab, Shift+Tab, Enter, Space, Arrow keys).</p>
          </div>

          <div className="p-4 bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.1)] rounded">
            <h4 className="text-[12px] font-medium text-white mb-2">🔊 Screen Reader Support</h4>
            <p className="text-[10px]">Compatible with popular screen readers including JAWS, NVDA, VoiceOver, and TalkBack. All images, icons, and non-text content have descriptive text alternatives.</p>
          </div>

          <div className="p-4 bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.1)] rounded">
            <h4 className="text-[12px] font-medium text-white mb-2">🎨 Color Contrast</h4>
            <p className="text-[10px]">All text and interactive elements meet WCAG 2.2 Level AA color contrast ratios (minimum 4.5:1 for normal text, 3:1 for large text).</p>
          </div>

          <div className="p-4 bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.1)] rounded">
            <h4 className="text-[12px] font-medium text-white mb-2">🎬 Motion Control</h4>
            <p className="text-[10px]">Respects the "prefers-reduced-motion" setting and provides an option to disable animations through our accessibility widget.</p>
          </div>
        </div>
      </Section>

      {/* Accessibility Widget */}
      <Section title="Accessibility Settings Widget">
        <div className="p-6 bg-[rgba(59,130,246,0.1)] border border-[rgba(59,130,246,0.3)] rounded">
          <div className="flex items-center gap-3 mb-4">
            <svg className="w-6 h-6 text-[#3b82f6]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <h3 className="text-[12px] font-medium text-white">Customizable Accessibility Options</h3>
          </div>
          <p className="text-[11px] mb-4">
            Click the accessibility button (bottom right corner) to access personalized settings:
          </p>
          <List items={[
            'Font Size Adjustment: Small, Medium, Large, Extra Large',
            'High Contrast Mode: Increased contrast for better visibility',
            'Dyslexia-Friendly Font: OpenDyslexic font option',
            'Color Themes: Light, Dark, and Grayscale modes',
            'Reduce Motion: Disable animations and transitions'
          ]} />
        </div>
      </Section>

      {/* Feedback */}
      <Section title="Feedback and Contact">
        <Paragraph>
          We welcome your feedback on the accessibility of DIZEE. Please let us know if you encounter any accessibility barriers:
        </Paragraph>
        
        <div className="mt-4 p-4 bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.1)] rounded">
          <p className="text-[11px]">
            <strong className="text-white">Email:</strong>{' '}
            <a href="mailto:accessibility@dizee.app" className="text-[#3b82f6] hover:text-[#60a5fa] transition-colors">
              accessibility@dizee.app
            </a>
          </p>
          <p className="text-[11px] mt-2">
            <strong className="text-white">Phone:</strong> [Your Contact Number]
          </p>
          <p className="text-[11px] mt-2">
            <strong className="text-white">Address:</strong> [Your Company Address]
          </p>
          <p className="text-[10px] mt-4 text-[rgba(255,255,255,0.6)]">
            We aim to respond to accessibility feedback within 2 business days and to propose a solution within 10 business days.
          </p>
        </div>
      </Section>

      {/* Testing Methodology */}
      <Section title="Testing and Evaluation">
        <Paragraph>We regularly test our website using the following methods:</Paragraph>
        <List items={[
          'Automated Testing: axe-core, Lighthouse, eslint-plugin-jsx-a11y',
          'Manual Testing: Keyboard navigation, screen reader testing (NVDA, VoiceOver)',
          'User Testing: Feedback from users with disabilities',
          'Browser Testing: Chrome, Firefox, Safari, Edge',
          'Device Testing: Desktop, tablet, and mobile devices'
        ]} />
      </Section>
    </LegalPageLayout>
  );
};

export default AccessibilityStatement;
